import { FC } from 'react';

import { TableCellProps } from '@servicetitan/table';
import { BodyText, Icon, Stack, Tag, Text, Tooltip } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';

import { CampaignsStore } from '@servicetitan/application-data/campaigns';

import { CallType, TollFreeNumberVerificationStatus } from '@servicetitan/application-data/telecom';
import { useFormatPhone } from '@servicetitan/react-hooks';
import classNames from 'classnames';
import { getMessagingValueToShow } from '../utils/messaging-cell-helper';

export const PhoneNumberCell: FC<TableCellProps> = props => {
    const formatPhone = useFormatPhone();
    return (
        <td style={props.style} className={props.className}>
            <Stack alignItems="center" spacing={2} wrap="nowrap">
                <Text size={2} bold className="c-neutral-400">
                    {formatPhone(props.dataItem[props.field!])}
                </Text>
                {props.dataItem.isDirectDial && (
                    <Tooltip text="Direct dial" direction="l">
                        <Icon name="contact_phone" size="20px" className="c-neutral-90" />
                    </Tooltip>
                )}
            </Stack>
        </td>
    );
};

export const CampaignCell: FC<TableCellProps> = props => {
    const [{ campaigns }] = useDependencies(CampaignsStore);

    if (props.dataItem.isDniAllocated) {
        return (
            <td data-cy={`allocated-to-dni-phone-number-${props.dataItem.friendlyName}`}>
                Allocated to DNI
            </td>
        );
    }

    const selectedOption = (id: number) => campaigns.find(option => option.id === id)?.name || '';
    return (
        <td data-cy={`campaign-name-phone-number-${props.dataItem.phoneNumber}`}>
            {selectedOption(props.dataItem[props.field!])}
        </td>
    );
};

export const CallContinuityNumberCell: FC<TableCellProps> = props => {
    const formatPhone = useFormatPhone();
    return (
        <td>
            <Stack alignItems="center" wrap="wrap">
                <Text size={2} className="c-neutral-400 m-r-2">
                    {formatPhone(props.dataItem[props.field!])}
                </Text>
                {props.dataItem.useDefaultCallForwarding && <Tag>Default</Tag>}
            </Stack>
        </td>
    );
};

export const IsTollFreeCell: FC<TableCellProps> = props => (
    <td>
        <Text size={2} className="c-neutral-400">
            {props.dataItem[props.field!] ? 'Yes' : 'No'}
        </Text>
    </td>
);

export const IncomingSmsCell: FC<TableCellProps> = props => (
    <td>
        <Stack justifyContent="center">
            {props.dataItem[props.field!] && (
                <Icon name="check" size="20px" className="c-blue-400" />
            )}
        </Stack>
    </td>
);

export const MessagingCell: FC<TableCellProps> = props => (
    <td style={props.style} className={props.className}>
        <Text size={2} className="c-neutral-400">
            {getMessagingValueToShow(props.dataItem[props.field!])}
        </Text>
    </td>
);

export const DefaultCallTypeCell: FC<TableCellProps> = props => (
    <td>{(props.dataItem[props.field!] as CallType) === CallType.NotLead ? 'Not Lead' : 'Lead'}</td>
);

const verificationStatusDependentProps: Record<TollFreeNumberVerificationStatus, [string, string]> =
    {
        [TollFreeNumberVerificationStatus.Unverified]: [
            'bg-red-400',
            'This number has not been verified and can not send text messages. You must register this number for texting',
        ],
        [TollFreeNumberVerificationStatus.Pending]: [
            'bg-yellow-600',
            'This number is in the verification process which can take up to 2 to 4 weeks to become verified',
        ],
        [TollFreeNumberVerificationStatus.Verified]: [
            'bg-green-500',
            'This number has been verified and can send text messages',
        ],
        [TollFreeNumberVerificationStatus.Other]: [
            'bg-yellow-600',
            'Please contact a ServiceTitan representative for details',
        ],
    };

export const TollFreeStatusCell: FC<TableCellProps> = props => {
    const statusValue = props.dataItem[props.field!];
    if (statusValue === undefined) {
        return <td />;
    }

    const statusValueBounded =
        TollFreeNumberVerificationStatus[statusValue] === undefined
            ? TollFreeNumberVerificationStatus.Other
            : (statusValue as TollFreeNumberVerificationStatus);

    const properties = verificationStatusDependentProps[statusValueBounded];

    return (
        <td>
            <Stack justifyContent="flex-start">
                <Tooltip hoverOnly text={properties[1]} direction="r">
                    <BodyText
                        className={classNames(
                            properties[0],
                            'p-half',
                            'c-white',
                            'border-radius-2'
                        )}
                    >
                        {TollFreeNumberVerificationStatus[statusValueBounded]}
                    </BodyText>
                </Tooltip>
            </Stack>
        </td>
    );
};
